/**
 * Pagaré de crédito · generador HTML imprimible.
 *
 * Estructura legal modelada sobre el pagaré tipo VANA Tech (libre de
 * protesto), adaptado a la realidad cooperativa de Neocoop.
 *
 * Branding:
 *   - LOGO_HORIZONTAL real importado del package @neocoop/cert-pdf
 *     (el mismo que usa el Certificado de Inversión)
 *   - Brand orange #FF5B04 + tipografía Inter
 *
 * Firma:
 *   - Si el asociado firmó desde la app móvil (SignaturePad de Expo),
 *     los paths SVG se guardan en credito.firma_paths con su canvas.
 *     Renderizamos la firma manuscrita real escalada al ancho del
 *     bloque de firma.
 *   - Si todavía no firmó (flujo legacy NeoCore), mostramos un bloque
 *     "Pendiente de firma" con metadata de aceptación electrónica
 *     según Decreto 47-2008.
 *
 * 14 cláusulas legales (A-N) cubriendo plazo, intereses, calendario,
 * forma de pago, mora con bandas, orden de aplicación, refinanciamiento,
 * cambio de condiciones, cargos, pago en exceso, cedibilidad,
 * vencimiento anticipado, costas y pactos procesales.
 */
import { supabase } from './supabase'
import { LOGO_HORIZONTAL } from '@neocoop/cert-pdf'

const COOP = {
  nombre: 'Cooperativa Integral de Ahorro y Crédito NEO, R.L.',
  nombreCorto: 'NEOCOOP R.L.',
  nit: '120719290',
  ingecop: 'Inscrita y autorizada por el INGECOP',
  direccion: 'Cubo Center, Antigua Guatemala, Sacatepéquez',
}

const fmtQ = (n: number) =>
  'Q.' + Number(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })

const fmtFecha = (iso: string) => {
  if (!iso) return '—'
  const d = new Date(String(iso).slice(0, 10) + 'T12:00:00')
  return d.toLocaleDateString('es-GT', { day: 'numeric', month: 'long', year: 'numeric' })
}

const fmtFechaCorta = (iso: string) => {
  if (!iso) return '—'
  const d = new Date(String(iso).slice(0, 10) + 'T12:00:00')
  return d.toLocaleDateString('es-GT', { day: '2-digit', month: '2-digit', year: 'numeric' })
}

// Cantidades a letras — sirve para montos del crédito emergente (Q500-Q2500).
const UNIDADES = ['cero','un','dos','tres','cuatro','cinco','seis','siete','ocho','nueve','diez',
  'once','doce','trece','catorce','quince','dieciséis','diecisiete','dieciocho','diecinueve','veinte']
const DECENAS = ['','','veinte','treinta','cuarenta','cincuenta','sesenta','setenta','ochenta','noventa']
function enteroAletras(n: number): string {
  if (n <= 20) return UNIDADES[n]
  if (n < 100) {
    const d = Math.floor(n / 10), u = n % 10
    if (n < 30) return 'veinti' + UNIDADES[u]
    return DECENAS[d] + (u ? ' y ' + UNIDADES[u] : '')
  }
  if (n === 100) return 'cien'
  if (n < 200) return 'ciento ' + enteroAletras(n - 100)
  if (n < 1000) {
    const c = Math.floor(n / 100), r = n % 100
    const cents = ['','ciento','doscientos','trescientos','cuatrocientos','quinientos','seiscientos','setecientos','ochocientos','novecientos'][c]
    return cents + (r ? ' ' + enteroAletras(r) : '')
  }
  if (n < 2000) return 'mil ' + (n - 1000 > 0 ? enteroAletras(n - 1000) : '')
  if (n < 1_000_000) {
    const miles = Math.floor(n / 1000), resto = n % 1000
    return enteroAletras(miles) + ' mil' + (resto ? ' ' + enteroAletras(resto) : '')
  }
  return String(n)
}
function cantidadEnLetras(n: number): string {
  const entero = Math.floor(n)
  const cents = Math.round((n - entero) * 100)
  return enteroAletras(entero) + ' quetzales con ' +
    (cents > 0 ? enteroAletras(cents) + ' centavos de quetzal' : 'cero centavos de quetzal')
}

/**
 * Hash determinístico ligero del crédito para mostrar como "huella" en
 * el bloque de firma electrónica. No es criptográfico — es solo una
 * marca visible que el operador puede comparar contra el sistema.
 */
function hashFirma(creditoId: string, verificationCode: string, firmaTs: string | null): string {
  const raw = creditoId + ':' + verificationCode + ':' + (firmaTs ?? '')
  let h = 0
  for (let i = 0; i < raw.length; i++) {
    h = (h * 31 + raw.charCodeAt(i)) | 0
  }
  return Math.abs(h).toString(16).toUpperCase().padStart(8, '0').slice(0, 8) +
         '-' +
         verificationCode.slice(0, 4).toUpperCase()
}

/**
 * Renderiza la firma manuscrita como SVG. Recibe los paths capturados
 * por el SignaturePad de la app (formato `SignatureData.paths`) y los
 * embebe en un SVG con viewBox del canvas original — así escala
 * proporcional al ancho del bloque en el pagaré sin perder la forma.
 */
function renderFirmaSvg(
  paths: string[] | null,
  canvasW: number | null,
  canvasH: number | null,
): string {
  if (!paths || paths.length === 0 || !canvasW || !canvasH) return ''
  const escaped = paths
    .filter((p) => typeof p === 'string' && p.length > 0)
    .map((p) => `<path d="${p}" />`)
    .join('')
  return (
    `<svg xmlns="http://www.w3.org/2000/svg" ` +
    `viewBox="0 0 ${canvasW} ${canvasH}" ` +
    `width="100%" preserveAspectRatio="xMidYMid meet" ` +
    `fill="none" stroke="#14171c" stroke-width="2.4" ` +
    `stroke-linecap="round" stroke-linejoin="round">` +
    `${escaped}` +
    `</svg>`
  )
}

export async function imprimirPagareCredito(creditoId: string): Promise<{ ok: true } | { ok: false; error: string }> {
  // Fetch credito + asociado + tipo + cuotas + bandas de mora + firma
  const { data: c, error } = await supabase
    .from('credito')
    .select(`
      id, capital_inicial, tasa, periodicidad_tasa, plazo_dias, metodo_cuota,
      total_cuotas, fecha_desembolso, fecha_primera_cuota, fecha_ultima_cuota,
      verification_code, firma_timestamp, firma_ip,
      firma_paths, firma_canvas_w, firma_canvas_h,
      asociado:asociado_id (
        id, no_asociado, nombre_completo, dpi,
        firma_paths, firma_canvas_w, firma_canvas_h,
        firma_actualizada_at, firma_actualizada_origen
      ),
      tipo:tipo_credito_id (id, nombre, mora_dias_gracia, redondea_aportacion)
    `)
    .eq('id', creditoId)
    .single()
  if (error || !c) return { ok: false, error: error?.message ?? 'Crédito no encontrado' }

  const { data: cuotas, error: cuoErr } = await supabase
    .from('credito_cuota')
    .select('numero, fecha_vencimiento, capital, interes, total')
    .eq('credito_id', creditoId)
    .order('numero', { ascending: true })
  if (cuoErr) return { ok: false, error: cuoErr.message }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const cr = c as any
  const asociado = cr.asociado
  const tipo = cr.tipo
  const refCorta = creditoId.slice(0, 8).toUpperCase()

  // Bandas de mora
  const { data: bandasData } = await supabase
    .from('tipo_credito_banda_mora')
    .select('dias_desde, dias_hasta, pct_recargo')
    .eq('tipo_credito_id', tipo.id)
    .order('orden', { ascending: true })
  const bandas = (bandasData ?? []) as Array<{ dias_desde: number; dias_hasta: number | null; pct_recargo: number }>

  const periodoLabelPlural: Record<string, string> = {
    diaria: 'diarios', semanal: 'semanales', quincenal: 'quincenales',
    mensual: 'mensuales', anual: 'anuales',
  }
  const tasaPct = Number(cr.tasa) * 100
  const tasaPctTxt = tasaPct.toFixed(2) + '%'
  const tasaPeriodicidad = periodoLabelPlural[cr.periodicidad_tasa] ?? cr.periodicidad_tasa
  const totalACobrar = (cuotas ?? []).reduce((s, q) => s + Number(q.total), 0)
  const interesTotal = (cuotas ?? []).reduce((s, q) => s + Number(q.interes), 0)
  const capital = Number(cr.capital_inicial)
  const numCuotas = cr.total_cuotas
  const plazoDias = cr.plazo_dias
  const plazoLetras = enteroAletras(plazoDias).toUpperCase()
  const fechaHoy = new Date()
  const fechaHoyLetras = fechaHoy.toLocaleDateString('es-GT', {
    day: 'numeric', month: 'long', year: 'numeric',
    timeZone: 'America/Guatemala',
  })

  const bandasTexto = bandas.map((b) => {
    if (b.dias_hasta === null) return `desde el día ${b.dias_desde} en adelante: ${Number(b.pct_recargo).toFixed(0)}% sobre la cuota vencida`
    return `entre los días ${b.dias_desde} y ${b.dias_hasta}: ${Number(b.pct_recargo).toFixed(0)}% sobre la cuota vencida`
  }).join('; ')

  // Redondeo a aportaciones: la cuota del CRÉDITO es capital+interés; el asociado
  // paga la cuota redondeada al quetzal y la diferencia se abona a su capital de
  // aportaciones (no forma parte de la deuda).
  const redondea = !!tipo?.redondea_aportacion
  const calendarioRows = (cuotas ?? []).map((q, idx) => {
    const totalCredito = Number(q.total)
    const cobrada = Math.ceil(totalCredito)
    const aporte = Math.round((cobrada - totalCredito) * 100) / 100
    const extra = redondea && aporte > 0
      ? ` · pago total ${fmtQ(cobrada)} (${fmtQ(aporte)} a aportaciones)`
      : ''
    return `
    <li>
      <span style="display:inline-block;width:20px;font-weight:700;">${idx + 1}.</span>
      ${fmtFechaCorta(q.fecha_vencimiento)} — ${fmtQ(totalCredito)} (capital ${fmtQ(Number(q.capital))} + intereses ${fmtQ(Number(q.interes))})${extra}
    </li>
  `}).join('')
  const notaAportacion = redondea
    ? `<p>El monto de cada cuota corresponde exclusivamente al capital e intereses del crédito.
       Adicionalmente, LA PARTE DEUDORA abonará por cada cuota la diferencia de redondeo al quetzal
       superior a su <span class="strong">capital de aportaciones</span> dentro de la Cooperativa, la cual
       NO forma parte de la obligación crediticia y se acredita como aportación del asociado.</p>`
    : ''

  // === Firma ===
  const firmaTs = cr.firma_timestamp ?? null
  const firmaIp = cr.firma_ip ?? null
  const firmada = !!firmaTs
  const fechaAceptacion = firmaTs ? new Date(firmaTs) : new Date(cr.fecha_desembolso + 'T12:00:00')
  const huella = hashFirma(creditoId, cr.verification_code, firmaTs)

  // Firma manuscrita SVG · jerarquía de fallback:
  //   1) credito.firma_paths       — firma específica del crédito (TODO #48)
  //   2) asociado.firma_paths      — firma actualizada por operador en NeoCore
  //   3) kyc.app_extras.signature  — firma original del onboarding (legacy)
  //   4) sin firma
  let firmaPaths: string[] | null = Array.isArray(cr.firma_paths) ? cr.firma_paths : null
  let firmaCanvasW: number | null = cr.firma_canvas_w ?? null
  let firmaCanvasH: number | null = cr.firma_canvas_h ?? null
  let firmaOrigen: 'credito' | 'asociado' | 'kyc' | null = firmaPaths ? 'credito' : null
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  let firmaActualizadaAt: string | null = null

  // Nivel 2 · firma actualizada en la ficha del asociado (NeoCore tablet)
  if (!firmaPaths && Array.isArray(asociado?.firma_paths) && asociado.firma_paths.length > 0) {
    firmaPaths = asociado.firma_paths
    firmaCanvasW = asociado.firma_canvas_w ?? null
    firmaCanvasH = asociado.firma_canvas_h ?? null
    firmaOrigen = 'asociado'
    firmaActualizadaAt = asociado.firma_actualizada_at ?? null
  }

  // Nivel 3 · firma del KYC original (legacy — antes de tener columna en asociado)
  if (!firmaPaths && asociado?.id) {
    const { data: kycRow } = await supabase
      .from('kyc_verification')
      .select('datos_extraidos')
      .eq('asociado_id', asociado.id)
      .order('created_at', { ascending: false })
      .limit(1)
      .maybeSingle()
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const sig = (kycRow as any)?.datos_extraidos?.app_extras?.signature
    if (sig && Array.isArray(sig.paths) && sig.paths.length > 0) {
      firmaPaths = sig.paths
      firmaCanvasW = Number(sig.width) || null
      firmaCanvasH = Number(sig.height) || null
      firmaOrigen = 'kyc'
    }
  }

  const firmaSvg = renderFirmaSvg(firmaPaths, firmaCanvasW, firmaCanvasH)
  const tieneFirmaManuscrita = firmaSvg.length > 0

  const html = `<!doctype html>
<html lang="es">
<head>
<meta charset="utf-8" />
<title>Pagaré · Crédito ${refCorta} · ${COOP.nombreCorto}</title>
<style>
  @page { size: letter portrait; margin: 0; }
  *{box-sizing:border-box}
  html,body{margin:0;padding:0;}
  body{
    font-family:'Inter',-apple-system,BlinkMacSystemFont,'Helvetica Neue',Arial,sans-serif;
    color:#14171c; background:#fff;
    -webkit-print-color-adjust:exact; print-color-adjust:exact;
    font-size:10pt; line-height:1.55;
  }
  .page{ padding: 18mm 18mm 14mm; }

  /* HEADER BRAND con logo real */
  .hd{
    display:flex; justify-content:space-between; align-items:flex-end;
    padding-bottom:14px; border-bottom:2.5px solid #FF5B04; margin-bottom:24px;
  }
  .hd .logo img{ height:46px; width:auto; display:block; }
  .hd .brand-sub{
    margin-top:6px; font-size:8pt; color:#9aa0a6;
    letter-spacing:0.14em; text-transform:uppercase; font-weight:700;
  }
  .hd .right{ text-align:right; }
  .hd .right .ref-k{
    font-size:8pt; color:#9aa0a6; text-transform:uppercase;
    letter-spacing:0.2em; font-weight:800;
  }
  .hd .right .ref-v{
    font-family:'IBM Plex Mono','SF Mono','Courier New',monospace;
    font-size:13pt; font-weight:700; color:#14171c; margin-top:3px;
    letter-spacing:0.04em;
  }
  .hd .right .ref-doc{
    margin-top:6px; font-size:8.5pt; color:#FF5B04;
    letter-spacing:0.18em; text-transform:uppercase; font-weight:700;
  }

  /* TÍTULOS */
  h1.title{
    margin:0; text-align:center; font-size:20pt; font-weight:800;
    letter-spacing:0.06em; text-transform:uppercase; color:#14171c;
  }
  p.subtitle{
    margin:4px 0 22px; text-align:center; font-size:11pt; font-weight:700;
    letter-spacing:0.08em; text-transform:uppercase; color:#FF5B04;
  }
  h2.section{
    margin:32px 0 14px; text-align:center; font-size:11pt; font-weight:800;
    letter-spacing:0.10em; text-transform:uppercase;
    padding-bottom:8px; border-bottom:1px solid #e5e7eb;
  }

  /* META destacada */
  .meta{
    margin:0 0 18px; padding:14px 16px;
    background:#fff8f1; border-left:3px solid #FF5B04; border-radius:0 8px 8px 0;
  }
  .meta p{ margin:0 0 5px; font-size:10pt; }
  .meta p:last-child{ margin-bottom:0; }
  .meta .k{ font-weight:800; }

  /* Body justificado */
  p, ol.clauses > li {
    text-align: justify; text-justify: inter-word; hyphens: auto;
  }
  .strong{ font-weight:700; }

  /* Cláusulas */
  ol.clauses{ margin:0; padding:0 0 0 22px; }
  ol.clauses > li{ margin:0 0 14px; padding:0 0 0 4px; }
  ol.clauses > li > .head{
    font-weight:800; text-transform:uppercase; letter-spacing:0.04em;
  }
  ol.cal{ margin:6px 0; padding:0 0 0 22px; list-style:none; }
  ol.cal li{ margin:3px 0; text-align:left; font-feature-settings:"tnum"; }

  /* === BLOQUE DE FIRMA === */
  .firma-wrap{
    margin-top:36px;
    border:1.5px solid #FF5B04;
    border-radius:10px;
    overflow:hidden;
    background:linear-gradient(135deg,#fff8f1 0%,#fff 100%);
    page-break-inside:avoid;
  }
  .firma-head{
    display:flex; justify-content:space-between; align-items:center;
    padding:12px 18px;
    border-bottom:1px dashed rgba(255,91,4,0.30);
  }
  .firma-head .title{
    font-size:9pt; font-weight:800; color:#FF5B04;
    letter-spacing:0.20em; text-transform:uppercase;
  }
  .firma-head .badge{
    display:inline-flex; align-items:center; gap:5px;
    padding:3px 10px; border-radius:99px;
    background:#FF5B04; color:#fff;
    font-size:8pt; font-weight:800; letter-spacing:0.12em;
    text-transform:uppercase;
  }
  .firma-head .badge.pend{ background:#d97706; }

  /* Layout interno: firma trazada a la izquierda, metadata a la derecha */
  .firma-body{
    display:grid; grid-template-columns: 1fr 1.05fr; gap:0;
  }
  .firma-stroke{
    padding:14px 18px; border-right:1px dashed rgba(255,91,4,0.30);
    display:flex; flex-direction:column; justify-content:space-between;
    min-height:160px;
  }
  .firma-stroke .label{
    font-size:8pt; font-weight:800; color:#9aa0a6;
    letter-spacing:0.16em; text-transform:uppercase;
  }
  .firma-stroke .canvas{
    flex:1; display:flex; align-items:center; justify-content:center;
    padding:8px 4px; min-height:90px;
  }
  .firma-stroke .canvas.empty{
    border:1px dashed #d4d4d8; border-radius:6px;
    color:#9aa0a6; font-size:9pt; font-style:italic;
  }
  .firma-stroke .nombre{
    margin-top:6px; padding-top:8px; border-top:1px solid #14171c;
    font-weight:800; font-size:11pt; letter-spacing:-0.01em;
  }
  .firma-stroke .dpi{ font-size:9pt; color:#3f3f46; margin-top:2px; }

  .firma-meta{ padding:14px 18px; }
  .firma-meta table{ width:100%; border-collapse:collapse; }
  .firma-meta table td{
    padding:5px 0; font-size:9.5pt; vertical-align:top;
  }
  .firma-meta table td.k{
    width:38%; color:#9aa0a6; font-size:8pt;
    text-transform:uppercase; letter-spacing:0.14em;
    font-weight:800; padding-right:10px;
  }
  .firma-meta table td.v{ font-weight:600; color:#14171c; }
  .firma-meta .huella{
    font-family:'IBM Plex Mono','SF Mono','Courier New',monospace;
    font-size:10pt; font-weight:700; color:#FF5B04; letter-spacing:0.08em;
  }
  .firma-nota{
    padding:10px 18px 14px; font-size:8pt; color:#6b6357;
    line-height:1.45; text-align:justify;
    border-top:1px dashed rgba(255,91,4,0.30);
  }

  /* FOOTER */
  .ft{
    margin-top:24px; padding-top:12px; border-top:1px solid #e5e7eb;
    font-size:8pt; color:#6b6357; text-align:center; letter-spacing:0.10em;
    text-transform:uppercase; font-weight:700;
  }
  .ft .ft-dir{
    margin-top:4px; font-size:7.5pt; color:#9aa0a6; letter-spacing:0.06em;
    text-transform:none; font-weight:500;
  }

  @media print { .noprint{ display:none; } }
  .noprint{
    position:fixed; bottom:16px; right:16px; padding:10px 18px;
    background:#FF5B04; color:#fff; border:0; border-radius:999px;
    font-size:13px; font-weight:700; cursor:pointer;
    box-shadow:0 4px 14px rgba(0,0,0,0.15); font-family:inherit;
  }
</style>
</head>
<body>
<div class="page">

<!-- HEADER con logo real Neocoop -->
<div class="hd">
  <div>
    <div class="logo"><img src="${LOGO_HORIZONTAL}" alt="Neocoop" /></div>
    <div class="brand-sub">${COOP.nombre}</div>
  </div>
  <div class="right">
    <div class="ref-k">Crédito N°</div>
    <div class="ref-v">${refCorta}</div>
    <div class="ref-doc">Pagaré · Libre de Protesto</div>
  </div>
</div>

<h1 class="title">Pagaré</h1>
<p class="subtitle">Libre de Protesto</p>

<div class="meta">
  <p><span class="k">A la orden o endoso de:</span> ${COOP.nombre}</p>
  <p><span class="k">Capital Adeudado:</span> ${fmtQ(capital)} (${cantidadEnLetras(capital)})</p>
  <p><span class="k">Plazo:</span> ${plazoDias} días</p>
  <p><span class="k">Vencimiento:</span> ${fmtFecha(cr.fecha_ultima_cuota)}</p>
</div>

<p>
  Yo, <span class="strong">${asociado?.nombre_completo ?? '—'}</span>,
  me identifico con el Documento Personal de Identificación con Código Único de Identificación
  <span class="strong">${asociado?.dpi ?? '—'}</span>
  extendido por el Registro Nacional de las Personas (RENAP), en adelante referido también
  simple e indistintamente como <span class="strong">"LA PARTE DEUDORA"</span>.
</p>

<p>
  Por el presente acto, <span class="strong">PROMETO INCONDICIONALMENTE PAGAR</span> a la orden
  o endoso de <span class="strong">${COOP.nombreCorto}</span>, en adelante referido simple e
  indistintamente como el <span class="strong">ACREEDOR</span>, la cantidad indicada como
  Capital Adeudado y cualquier otro interés, costo y/o gasto que resulte aplicable (en adelante
  conjuntamente <span class="strong">"Suma Adeudada"</span>).
</p>

<h2 class="section">Términos y Condiciones del Pagaré</h2>

<ol class="clauses" type="A">

  <li>
    <span class="head">Plazo.</span>
    El plazo del presente pagaré será de <span class="strong">${plazoLetras} (${plazoDias}) DÍAS</span>,
    contados a partir de la fecha de aceptación y firma del presente instrumento. El ACREEDOR
    podrá, en cualquier momento después del vencimiento inicial del pagaré, exigir el pago del
    capital, intereses, intereses moratorios, comisiones y cargos que correspondan a LA PARTE
    DEUDORA, sin perjuicio de cualquier plazo de gracia otorgado.
  </li>

  <li>
    <span class="head">Intereses.</span>
    LA PARTE DEUDORA reconoce y se obliga a pagar el capital y los intereses aplicables. Sobre
    el capital adeudado, se generará un interés del <span class="strong">${tasaPctTxt} ${tasaPeriodicidad}</span>
    sobre el capital adeudado, por lo que se deberá pagar un total de
    <span class="strong">${fmtQ(interesTotal)}</span> (${cantidadEnLetras(interesTotal)}) en concepto de intereses.
    Los intereses generados por operaciones cooperativas se encuentran exentos del Impuesto al
    Valor Agregado conforme a la legislación cooperativa guatemalteca.
    <br/><br/>
    <span class="head">Amortizaciones.</span>
    LA PARTE DEUDORA deberá pagar el capital y los intereses aplicables en
    <span class="strong">${numCuotas === 1 ? 'UNA (1)' : numCuotas === 2 ? 'DOS (2)' : `${numCuotas}`} cuota${numCuotas === 1 ? '' : 's'}</span>
    ${numCuotas === 1 ? '' : `${periodoLabelPlural[cr.periodicidad_tasa] ?? ''} consecutivas`}.
    En ese sentido, LA PARTE DEUDORA deberá pagar la cantidad de
    <span class="strong">${fmtQ(capital)}</span> (${cantidadEnLetras(capital)}) en concepto de capital,
    más <span class="strong">${fmtQ(interesTotal)}</span> (${cantidadEnLetras(interesTotal)}) en concepto de intereses,
    para un total de <span class="strong">${fmtQ(totalACobrar)}</span> (${cantidadEnLetras(totalACobrar)}).
    Para el cálculo y pago de cada cuota, el ACREEDOR podrá redondear los decimales al centavo
    más cercano.
  </li>

  <li>
    <span class="head">Calendario de Pagos.</span>
    Las cuotas se deberán pagar en las siguientes fechas:
    <ol class="cal">
      ${calendarioRows}
    </ol>
    ${notaAportacion}
  </li>

  <li>
    <span class="head">Forma y Lugar de Pago.</span>
    Todos los pagos que, por cualquier naturaleza, procedan conforme a este pagaré deberán
    efectuarse en las oficinas de ${COOP.nombreCorto} ubicadas en ${COOP.direccion}, o mediante
    depósito o transferencia bancaria a la cuenta que indique el ACREEDOR, o por débito a la
    cuenta de ahorro corriente que LA PARTE DEUDORA mantiene con la cooperativa. La Suma
    Adeudada deberá pagarse al ACREEDOR en la fecha de vencimiento, sin necesidad de protesto,
    aviso de falta de pago, requerimiento de pago y actos similares exigidos para la vigencia y
    ejecutabilidad de los derechos y acciones aquí contenidos.
    <br/><br/>
    El ACREEDOR podrá aceptar pagos parciales en los casos que considere apropiado, sin que esto
    limite o implique una renuncia a su derecho a exigir el pago completo o cobrar judicialmente
    el pagaré.
  </li>

  <li>
    <span class="head">Intereses Moratorios.</span>
    Si LA PARTE DEUDORA no paga el capital y/o intereses adeudados en la cuota estipulada, y
    una vez transcurridos los <span class="strong">${tipo.mora_dias_gracia} días de gracia</span> posteriores
    al vencimiento de la cuota, el presente pagaré generará intereses moratorios conforme al
    siguiente esquema de bandas: ${bandasTexto}. Los intereses moratorios se calcularán sobre el
    monto de la cuota vencida y no sobre el capital total. Los intereses moratorios no serán
    capitalizados. El ACREEDOR podrá condonar total o parcialmente los intereses moratorios
    aplicables, a su total discreción.
  </li>

  <li>
    <span class="head">Orden de Pagos.</span>
    Todo pago que realice LA PARTE DEUDORA se aplicará, en primer lugar, a los intereses
    moratorios que se hayan generado y, al liquidar el monto correspondiente, cualquier pago se
    aplicará a los intereses contractuales y, por último, al capital adeudado.
  </li>

  <li>
    <span class="head">Refinanciamiento.</span>
    Sin perjuicio de los derechos que incorpora el presente pagaré, el ACREEDOR podrá recibir
    solicitudes para el refinanciamiento de la Suma Adeudada y, a su sola discreción, autorizar
    o no la solicitud.
  </li>

  <li>
    <span class="head">Cambio de Condiciones.</span>
    En caso que algún ente regulador o entidad autorizada estableciera tasas de interés máximas
    para operaciones aquí previstas, los intereses que devengará este contrato serán los
    intereses máximos que fije la Instancia Nacional de Cooperativas (INGECOP) o entidad
    sustituta, a menos que el ACREEDOR decida cobrar una tasa menor.
  </li>

  <li>
    <span class="head">Cargos.</span>
    Por cargos se entiende cualquier costo de transacción necesario para que LA PARTE DEUDORA
    pueda obtener el capital o realizar los pagos, incluyendo comisiones bancarias, gastos
    administrativos y cualquier otro cargo de un tercero.
  </li>

  <li>
    <span class="head">Pago en Exceso.</span>
    En caso LA PARTE DEUDORA realice un pago en exceso, el ACREEDOR deberá realizar una
    devolución en un plazo que no exceda de cuarenta y cinco (45) días hábiles, salvo que se
    acuerde acreditar el monto a su cuenta de ahorro corriente con la cooperativa.
  </li>

  <li>
    <span class="head">Cedibilidad.</span>
    El ACREEDOR podrá transferir y negociar sus derechos provenientes del presente pagaré sin
    necesidad de previa autorización, debiendo únicamente notificar de la cesión a LA PARTE
    DEUDORA. LA PARTE DEUDORA no podrá ceder o transferir sus derechos u obligaciones derivados
    del presente pagaré sin autorización previa, expresa y por escrito del ACREEDOR.
  </li>

  <li>
    <span class="head">Vencimiento Anticipado.</span>
    El ACREEDOR podrá dar por vencido el plazo del presente pagaré y exigir el pago de la
    totalidad de la Suma Adeudada en los casos siguientes:
    <ol style="margin:6px 0 0 18px;padding:0;">
      <li>Si los bienes de LA PARTE DEUDORA fueren objeto de embargo o en caso de insolvencia o quiebra.</li>
      <li>Si LA PARTE DEUDORA tiene un cambio desfavorable de sus condiciones financieras que resulte en falta de capacidad de pago.</li>
      <li>Por incumplimiento en el pago de otros créditos ante el ACREEDOR o cualquier otra obligación que tenga como asociado.</li>
      <li>Si LA PARTE DEUDORA perdiere su condición de asociado de la cooperativa por cualquier causa.</li>
    </ol>
  </li>

  <li>
    <span class="head">Costas y Gastos.</span>
    Las costas judiciales, honorarios y cualquier otro gasto que se genere en virtud de la
    ejecución del presente pagaré serán por cuenta única y exclusiva de LA PARTE DEUDORA.
  </li>

  <li>
    <span class="head">Pactos Procesales.</span>
    Para el efectivo cumplimiento de sus obligaciones, LA PARTE DEUDORA expresamente:
    <ol style="margin:6px 0 0 18px;padding:0;">
      <li>Renuncia a cualquier cobro o requerimiento, necesidad de protesto del presente pagaré (por lo que el presente pagaré se emite "libre de protesto"), aviso de falta de pago y actos similares.</li>
      <li>Renuncia al fuero de su domicilio y se somete a los tribunales que elija el ACREEDOR.</li>
      <li>Acepta como buenas y válidas las notificaciones que se le hicieren mediante la aplicación móvil de Neocoop, WhatsApp o correo electrónico al número y dirección registrados en su ficha.</li>
      <li>Acepta como buenas y exactas las cuentas que sobre la presente obligación se le formulen.</li>
    </ol>
  </li>

</ol>

<h2 class="section">Comunicaciones Electrónicas</h2>

<p>
  Para todo lo relativo al presente título de crédito, se tomará la información establecida en
  el presente documento y la registrada en la aplicación móvil de Neocoop o el canal de
  WhatsApp asociado. Las partes reconocen la plena validez de cualquier comunicación y
  aceptación electrónica mediante correo electrónico, aplicación móvil o similar, en virtud del
  <span class="strong">Decreto 47-2008 — Ley para el Reconocimiento de las Comunicaciones y
  Firmas Electrónicas</span>.
</p>

<p style="margin-top:14px;">
  En virtud de lo anterior, acepto el presente pagaré en la Ciudad de Guatemala, el
  <span class="strong">${fechaHoyLetras}</span>.
</p>

<!-- === BLOQUE FIRMA === -->
<div class="firma-wrap">
  <div class="firma-head">
    <div class="title">Firma Electrónica · Decreto 47-2008</div>
    <div class="badge${firmada || tieneFirmaManuscrita ? '' : ' pend'}">
      ${firmada || tieneFirmaManuscrita ? '✓ Aceptado' : '⏱ Pendiente'}
    </div>
  </div>

  <div class="firma-body">
    <!-- Trazo manuscrito a la izquierda -->
    <div class="firma-stroke">
      <div class="label">Firma de la parte deudora</div>
      <div class="canvas${tieneFirmaManuscrita ? '' : ' empty'}">
        ${tieneFirmaManuscrita
          ? firmaSvg
          : 'Sin firma manuscrita registrada'}
      </div>
      <div>
        <div class="nombre">${asociado?.nombre_completo ?? '—'}</div>
        <div class="dpi">DPI ${asociado?.dpi ?? '—'} · Asociado N° ${asociado?.no_asociado ?? '—'}</div>
      </div>
    </div>

    <!-- Metadata electrónica a la derecha -->
    <div class="firma-meta">
      <table>
        <tr>
          <td class="k">Aceptación</td>
          <td class="v">${fechaAceptacion.toLocaleString('es-GT', {
            day: '2-digit', month: 'long', year: 'numeric',
            hour: '2-digit', minute: '2-digit',
            timeZone: 'America/Guatemala',
          })}</td>
        </tr>
        <tr>
          <td class="k">Zona horaria</td>
          <td class="v">América/Guatemala (UTC-6)</td>
        </tr>
        ${firmaIp ? `
        <tr>
          <td class="k">IP de origen</td>
          <td class="v">${firmaIp}</td>
        </tr>` : ''}
        <tr>
          <td class="k">Medio</td>
          <td class="v">${
            firmaOrigen === 'credito'
              ? 'Firma manuscrita aceptada en app móvil'
              : firmaOrigen === 'asociado'
                ? `Firma actualizada en NeoCore${firmaActualizadaAt ? ` · ${new Date(firmaActualizadaAt).toLocaleDateString('es-GT', { day: '2-digit', month: 'short', year: 'numeric', timeZone: 'America/Guatemala' })}` : ''}`
                : firmaOrigen === 'kyc'
                  ? 'Firma de identidad registrada al asociarse'
                  : 'Aceptación digital en NeoCore'
          }</td>
        </tr>
        <tr>
          <td class="k">Huella</td>
          <td class="v"><span class="huella">${huella}</span></td>
        </tr>
        <tr>
          <td class="k">Crédito</td>
          <td class="v">${refCorta}</td>
        </tr>
        <tr>
          <td class="k">Verificable en</td>
          <td class="v">neocoop.gt</td>
        </tr>
      </table>
    </div>
  </div>

  <div class="firma-nota">
    El presente documento ha sido aceptado por el firmante por medios electrónicos en virtud del
    Decreto 47-2008 — Ley para el Reconocimiento de las Comunicaciones y Firmas Electrónicas. La
    huella corresponde al hash determinístico del crédito y su código de verificación; puede
    contrastarse contra el registro de Neocoop. La autenticidad del documento puede verificarse
    en neocoop.gt o escaneando el código QR del crédito.
  </div>
</div>

<div class="ft">
  ${COOP.nombreCorto} · NIT ${COOP.nit} · ${COOP.ingecop}
  <div class="ft-dir">${COOP.direccion}</div>
</div>

</div>
<!-- /.page -->

<button class="noprint" onclick="window.print()">Imprimir</button>
<script>setTimeout(function(){ window.print(); }, 400);</script>

</body>
</html>`

  const w = window.open('', '_blank')
  if (!w) return { ok: false, error: 'No se pudo abrir la ventana de impresión (popup bloqueado)' }
  w.document.open()
  w.document.write(html)
  w.document.close()
  return { ok: true }
}
